/**
 * F12 Sprint E — CopilotPalette (item #327)
 *
 * Cmd+I (Mac) / Ctrl+I (Win) activated chat palette. (Cmd+J → global search / CommandPalette #10)
 * Renders as a centered modal overlay with chat history + input.
 * HITL approval triggers are shown inline.
 *
 * Registered globally via CopilotProvider (in App.tsx or layout root).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { X, Pin, PinOff, Send, Loader2, CheckCircle, XCircle, Wrench, ChevronDown, ChevronRight, BarChart2, Activity } from 'lucide-react';
import { useCopilotStore, type CopilotMessage } from '../../stores/copilotStore.js';
import { copilotService } from '../../services/copilot.service.js';

// ─── ToolCall Bubble ──────────────────────────────────────────────────────────

function ToolCallBubble({ toolCalls }: { toolCalls: CopilotMessage['toolCalls'] }) {
  const [expanded, setExpanded] = useState(false);
  if (!toolCalls || toolCalls.length === 0) return null;
  return (
    <div className="mt-1 rounded border border-violet-200 bg-violet-50 px-2 py-1 text-xs dark:border-violet-800 dark:bg-violet-950">
      <button
        type="button"
        className="flex items-center gap-1 text-violet-700 dark:text-violet-300"
        onClick={() => setExpanded((v) => !v)}
      >
        <Wrench className="h-3 w-3" />
        <span>{toolCalls.length} tool call{toolCalls.length > 1 ? 's' : ''}</span>
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {expanded && (
        <ul className="mt-1 space-y-0.5 text-violet-800 dark:text-violet-200">
          {toolCalls.map((tc, i) => (
            <li key={i} className="font-mono">
              {tc.tool}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── HITL Approval Banner ─────────────────────────────────────────────────────

function HitlBanner({
  correlationId,
  toolName,
  onResolve,
}: {
  correlationId: string;
  toolName: string;
  onResolve: (approved: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);

  const handle = async (approved: boolean) => {
    setLoading(true);
    try {
      await copilotService.approve(correlationId, approved);
      onResolve(approved);
    } catch {
      onResolve(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700 dark:bg-amber-950">
      <span className="flex-1 text-amber-800 dark:text-amber-200">
        <strong>{toolName}</strong> çalıştırmak için onayınız gerekli.
      </span>
      <button
        type="button"
        disabled={loading}
        onClick={() => handle(true)}
        className="flex items-center gap-1 rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
      >
        <CheckCircle className="h-3 w-3" /> Onayla
      </button>
      <button
        type="button"
        disabled={loading}
        onClick={() => handle(false)}
        className="flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
      >
        <XCircle className="h-3 w-3" /> Reddet
      </button>
      {loading && <Loader2 className="h-4 w-4 animate-spin text-amber-600" />}
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────────────────────

function MessageBubble({ msg, onApprovalResolve }: {
  msg: CopilotMessage;
  onApprovalResolve: (correlationId: string, approved: boolean) => void;
}) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
          isUser
            ? 'bg-violet-600 text-white'
            : 'bg-gray-100 text-gray-900 dark:bg-zinc-800 dark:text-gray-100'
        }`}
      >
        <p className="whitespace-pre-wrap">{msg.content}</p>
        {!isUser && <ToolCallBubble toolCalls={msg.toolCalls} />}
        {msg.pendingApproval && (
          <HitlBanner
            correlationId={msg.pendingApproval.correlationId}
            toolName={msg.pendingApproval.toolName}
            onResolve={(approved) => onApprovalResolve(msg.pendingApproval!.correlationId, approved)}
          />
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CopilotPalette() {
  const { isOpen, close, isPinned, togglePin, messages, isLoading, addMessage, updateLastMessage, setSessionId, sessionId, setLoading, clearHistory } =
    useCopilotStore();

  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    setInput('');
    setLoading(true);

    addMessage({ role: 'user', content: text });

    try {
      const result = await copilotService.ask(text, sessionId);

      // Save session ID for conversation continuity
      if (!sessionId && result.conversationId) {
        setSessionId(String(result.conversationId));
      }

      // Check for HITL pending signal in content
      const hitlMatch = result.content.match(/correlationId:\s*([a-f0-9-]{36})/i);
      const toolNameMatch = result.content.match(/tool["\s]+([a-z.]+)["\s]+HITL/i);

      addMessage({
        role: 'assistant',
        content: result.content,
        toolCalls: result.toolCalls,
        pendingApproval: hitlMatch
          ? { correlationId: hitlMatch[1]!, toolName: toolNameMatch?.[1] ?? 'write-tool' }
          : undefined,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bir hata oluştu.';
      addMessage({ role: 'assistant', content: `Hata: ${msg}` });
    } finally {
      setLoading(false);
    }
  }, [input, isLoading, sessionId, addMessage, setSessionId, setLoading]);

  const handleApprovalResolve = useCallback((_correlationId: string, _approved: boolean) => {
    // Clear pendingApproval from last message on resolution
    updateLastMessage({ pendingApproval: undefined });
  }, [updateLastMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      close();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-24"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      data-testid="copilot-palette"
    >
      <div className="flex w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-zinc-900" style={{ maxHeight: '70vh' }}>
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-zinc-700">
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Studio Copilot</span>
          <span className="ml-1 rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700 dark:bg-violet-900 dark:text-violet-300">
            AI
          </span>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              title={isPinned ? 'Unpin sidebar' : 'Pin to sidebar'}
              onClick={togglePin}
              className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-zinc-800"
            >
              {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
            <button
              type="button"
              title="Clear history"
              onClick={clearHistory}
              className="rounded p-1.5 text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800"
            >
              Temizle
            </button>
            <button
              type="button"
              onClick={close}
              className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-zinc-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto px-4 py-3" style={{ minHeight: 0 }}>
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-gray-400 dark:text-gray-500">
              <p className="font-medium">Merhaba! Studio Copilot burada.</p>
              <p className="text-xs">Agent/pipeline oluştururken yardımcı olabilirim.</p>
              <ul className="mt-2 space-y-1 text-xs text-gray-300 dark:text-gray-600">
                <li>"agent-1 ID'li agent'ın config'ini göster"</li>
                <li>"atlas'ta pipeline pattern ara"</li>
                <li>"yeni bir developer agent oluştur"</li>
              </ul>
            </div>
          )}
          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} onApprovalResolve={handleApprovalResolve} />
          ))}
          {isLoading && (
            <div className="flex justify-start mb-3">
              <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-4 py-2 dark:bg-zinc-800">
                <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Düşünüyor...</span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Observability deep-link footer (visible when conversation is active) */}
        {sessionId && (
          <div className="flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-4 py-1.5 text-[11px] text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/60 dark:text-gray-400">
            <span className="font-medium text-gray-400 dark:text-gray-500">ID {sessionId}</span>
            <a
              href={`/ai/observability/cost?conversationId=${sessionId}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              <BarChart2 className="h-3 w-3" />
              Maliyet
            </a>
            <a
              href={`/ai/observability/traces?conversationId=${sessionId}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              <Activity className="h-3 w-3" />
              Trace
            </a>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-gray-200 px-3 py-2 dark:border-zinc-700">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Bir şey sorun... (Enter göndermek, Shift+Enter yeni satır)"
              rows={2}
              className="flex-1 resize-none rounded-lg border border-gray-200 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-violet-400 focus:outline-none dark:border-zinc-700 dark:text-gray-100 dark:placeholder-gray-500"
            />
            <button
              type="button"
              disabled={!input.trim() || isLoading}
              onClick={handleSend}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
